import { inject, Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState, getHealthSummary } from '../../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../../user/store/selectors/user.selectors';
import { PatientProblemsService } from '../../../services/patient-problems/patient-problems.service';
import {
  getAllPatientProblems,
  getAllPatientProblemsFailure,
  getAllPatientProblemsSuccess,
  getPatientProblems,
  getPatientProblemsFailure,
  getPatientProblemsSuccess,
  recordPatientProblemPrintEvent,
  recordPatientProblemPrintEventFailure,
  recordPatientProblemPrintEventSuccess,
  setPageNumber,
} from '../actions/patient-problem.actions';
import {
  selectCacheLoadedAt,
  selectCachePatientId,
  selectCurrentCount,
  selectIsFullDatasetLoaded,
  selectPageNumber,
  selectPageSize,
  selectPatientProblems,
} from '../selectors/patient-problem.selector';

@Injectable()
export class PatientProblemEffects {
  private readonly FULL_DATASET_CACHE_TTL_MS = 5 * 60 * 1000;
  private actions$ = inject(Actions);
  private patientProblemsService = inject(PatientProblemsService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientProblems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientProblems, setPageNumber, getHealthSummary),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
        this.store.select(selectCurrentCount),
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientProblems),
      ]),
      mergeMap(
        ([
          action,
          patientId,
          pageNumber,
          pageSize,
          currentCount,
          isFullDatasetLoaded,
          cachePatientId,
          cacheLoadedAt,
          cachedPatientProblems,
        ]) => {
          const hasFreshFullDatasetCache =
            patientId != null &&
            isFullDatasetLoaded &&
            cachePatientId === patientId &&
            this.isCacheFresh(cacheLoadedAt);

          // The Health Summary widget needs the full problem set so it can show an accurate
          // active (Acute/Chronic) count, so load the full dataset rather than a 5-row page.
          if (action.type === getHealthSummary.type) {
            if (hasFreshFullDatasetCache) {
              return of(
                getAllPatientProblemsSuccess({
                  patientProblems: cachedPatientProblems,
                  patientId,
                  loadedAt: cacheLoadedAt ?? Date.now(),
                })
              );
            }

            return this.patientProblemsService.getAllPatientProblemsByPatientId(patientId).pipe(
              takeUntil(this.cancelRequest$),
              map(patientProblems =>
                getAllPatientProblemsSuccess({
                  patientProblems,
                  patientId,
                  loadedAt: Date.now(),
                })
              ),
              catchError(err => of(getAllPatientProblemsFailure({ error: err })))
            );
          }

          if (hasFreshFullDatasetCache) {
            if (isDevMode()) {
              console.debug('[Problems][Cache] Full-dataset cache hit; skipping paged problems API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientProblemsSuccess({
                patientProblems: cachedPatientProblems,
                pageNumber: 1,
                isAtEndOfData: true,
                totalRecordCount: currentCount,
              })
            );
          }

          return this.patientProblemsService.postPatientProblems(patientId, pageNumber, pageSize).pipe(
            takeUntil(this.cancelRequest$),
            map(response => {
              let isAtEndOfData = false;
              const totalRecordCount = response.totalRecordCount;
              let currentPage = pageNumber || 1;
              let patientProblems = response.result;

              if (patientProblems == null || patientProblems.length == 0) {
                patientProblems = [];
                currentPage--;
                isAtEndOfData = true;
              } else if (currentCount + patientProblems.length === totalRecordCount) {
                isAtEndOfData = true;
              }

              return getPatientProblemsSuccess({
                patientProblems,
                pageNumber: currentPage <= 0 ? 1 : currentPage,
                isAtEndOfData,
                totalRecordCount,
              });
            }),
            catchError(err => {
              return of(getPatientProblemsFailure({ error: err }));
            })
          );
        }
      )
    );
  });

  getAllPatientProblems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllPatientProblems),
      concatLatestFrom(() => [
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientProblems),
      ]),
      mergeMap(([{ patientId }, isFullDatasetLoaded, cachePatientId, cacheLoadedAt, patientProblems]) => {
        if (isFullDatasetLoaded && cachePatientId === patientId && this.isCacheFresh(cacheLoadedAt)) {
          if (isDevMode()) {
            console.debug('[Problems][Cache] Full-dataset cache hit; skipping GetAllByPatientId call.', {
              patientId,
              count: patientProblems.length,
            });
          }
          return of(
            getAllPatientProblemsSuccess({
              patientProblems,
              patientId,
              loadedAt: cacheLoadedAt ?? Date.now(),
            })
          );
        }

        return this.patientProblemsService.getAllPatientProblemsByPatientId(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientProblems =>
            getAllPatientProblemsSuccess({
              patientProblems,
              patientId,
              loadedAt: Date.now(),
            })
          ),
          catchError(err => of(getAllPatientProblemsFailure({ error: err })))
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientProblemPrintEvent),
      mergeMap(({ patientId, patientProblemIds }) =>
        this.patientProblemsService.postPrintEvent(patientId, patientProblemIds).pipe(
          map(() => recordPatientProblemPrintEventSuccess()),
          catchError(err => of(recordPatientProblemPrintEventFailure({ error: err })))
        )
      )
    );
  });

  private isCacheFresh(cacheLoadedAt: number | null): boolean {
    if (!cacheLoadedAt) {
      return false;
    }

    return Date.now() - cacheLoadedAt <= this.FULL_DATASET_CACHE_TTL_MS;
  }
}
